import { Link } from '@tanstack/react-router';
import { History, MoreHorizontal, PackagePlus, Target } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { InventoryLevel, StockLocation } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';

import { useStore } from '@/shared/store/store-context';

import { isLowStock } from '../schemas/inventory-rules';

import { LowStockBadge } from './stock-badges';

export type LevelAction = 'adjust' | 'reorderPoint' | 'movements';

const numeric = (value: number) => <span className="tabular-nums">{value}</span>;

/**
 * Column model of the stock levels table; `meta` ties columns to the list's sorts and filters.
 * The location column shows only when the store has more than one location.
 */
export function useInventoryColumns(
  onAction: (action: LevelAction, level: InventoryLevel) => void,
  locations: readonly StockLocation[],
) {
  const { t } = useTranslation('inventory');
  const format = useFormat();
  const { can } = usePermissions();
  const canWrite = can('inventory.write');
  const storeId = useStore().id;

  const columns = useMemo<Array<DataTableColumnDef<InventoryLevel>>>(
    () => [
      {
        id: 'variant',
        header: t('columns.variant'),
        cell: ({ row }) => (
          <Link
            to="/s/$storeId/inventory/$variantId"
            params={{ storeId, variantId: row.original.variantId }}
            className="flex min-w-0 flex-col hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="truncate font-medium">{row.original.productTitle}</span>
            <span className="text-muted-foreground truncate text-xs">
              {row.original.variantTitle}
            </span>
          </Link>
        ),
        meta: { label: t('columns.variant'), filterKey: 'productId', lockVisibility: true },
      },
      {
        id: 'location',
        header: t('columns.location'),
        cell: ({ row }) => locations.find((l) => l.id === row.original.locationId)?.name ?? '—',
        meta: { label: t('columns.location'), filterKey: 'locationId' },
        size: 150,
      },
      {
        id: 'sku',
        header: t('columns.sku'),
        cell: ({ row }) =>
          row.original.sku ? (
            <code className="text-xs">{row.original.sku}</code>
          ) : (
            <span className="text-muted-foreground">{t('levels.noSku')}</span>
          ),
        meta: { label: t('columns.sku'), sortField: 'sku' },
        size: 170,
      },
      {
        id: 'onHand',
        header: t('columns.onHand'),
        cell: ({ row }) => numeric(row.original.onHand),
        meta: { label: t('columns.onHand'), sortField: 'onHand', align: 'end' },
        size: 90,
      },
      {
        id: 'reserved',
        header: t('columns.reserved'),
        cell: ({ row }) => numeric(row.original.reserved),
        meta: { label: t('columns.reserved'), align: 'end' },
        size: 90,
      },
      {
        id: 'available',
        header: t('columns.available'),
        cell: ({ row }) => (
          <span className="flex items-center justify-end gap-2">
            {isLowStock(row.original) ? <LowStockBadge /> : null}
            <span className="font-medium tabular-nums">{row.original.available}</span>
          </span>
        ),
        meta: {
          label: t('columns.available'),
          sortField: 'available',
          filterKey: 'available',
          align: 'end',
        },
        size: 160,
      },
      {
        id: 'reorderPoint',
        header: t('columns.reorderPoint'),
        cell: ({ row }) =>
          canWrite ? (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto flex tabular-nums"
              aria-label={t('reorderPoint.edit', {
                sku: row.original.sku ?? row.original.variantTitle,
              })}
              onClick={(e) => {
                e.stopPropagation();
                onAction('reorderPoint', row.original);
              }}
            >
              {row.original.reorderPoint ?? '—'}
            </Button>
          ) : (
            <span className="block text-right tabular-nums">
              {row.original.reorderPoint ?? '—'}
            </span>
          ),
        meta: {
          label: t('columns.reorderPoint'),
          filterKey: 'lowStock',
          align: 'end',
          tour: 'inventory.levels.reorder-point',
        },
        size: 110,
      },
      {
        id: 'updatedAt',
        header: t('columns.updated'),
        cell: ({ row }) =>
          row.original.updatedAt ? (
            <span className="text-muted-foreground" title={format.utc(row.original.updatedAt)}>
              {format.relative(row.original.updatedAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('levels.never')}</span>
          ),
        meta: { label: t('columns.updated'), sortField: 'updatedAt' },
        size: 120,
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">{t('columns.actions')}</span>,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t('actions.menu', {
                  sku: row.original.sku ?? row.original.variantTitle,
                })}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {canWrite ? (
                <>
                  <DropdownMenuItem onSelect={() => onAction('adjust', row.original)}>
                    <PackagePlus aria-hidden />
                    {t('actions.adjust')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => onAction('reorderPoint', row.original)}>
                    <Target aria-hidden />
                    {t('actions.reorderPoint')}
                  </DropdownMenuItem>
                </>
              ) : null}
              <DropdownMenuItem onSelect={() => onAction('movements', row.original)}>
                <History aria-hidden />
                {t('actions.movements')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        meta: {
          label: t('columns.actions'),
          lockVisibility: true,
          tour: 'inventory.levels.row-actions',
        },
        size: 56,
      },
    ],
    [canWrite, format, locations, onAction, storeId, t],
  );
  return useMemo(
    () => (locations.length > 1 ? columns : columns.filter((column) => column.id !== 'location')),
    [columns, locations.length],
  );
}
